import { useEffect, useState } from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { useTitle } from '../lib/title'

type Meeting = {
  id: string
  title: string
  endAt: string
  status: string
  attendeeCount: number
  acceptedCount: number
  voteCount: number
  score?: number
}

type Upcoming = {
  id: string
  title: string
  endAt: string
  attendeeCount: number
  acceptedCount: number
}

type DashboardData = {
  past: Meeting[]
  upcoming: Upcoming[]
}

export default function Dashboard() {
  useTitle('Your meetings')
  const [data, setData] = useState<DashboardData | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/me')
      .then(r => {
        if (r.status === 401) { window.location.href = '/'; return null }
        if (!r.ok) throw new Error(`me HTTP ${r.status}`)
        return r.json()
      })
      .then(d => d && setEmail(d.email))
      .catch(e => setError(String(e)))
  }, [])

  useEffect(() => {
    if (!email) return
    fetch('/api/dashboard/meetings')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(setData)
      .catch(e => setError(String(e)))
  }, [email])

  const signOut = () => {
    fetch('/auth/google/signout', { method: 'POST' }).finally(() => window.location.href = '/')
  }

  const shell = (body: React.ReactNode) => (
    <>
      <Header>
        {email ? <span className="who">{email}</span> : null}
        {email ? <button type="button" onClick={signOut}>Sign out</button> : null}
      </Header>
      <main className="wrap dash">{body}</main>
      <Footer />
    </>
  )

  if (error) return shell(<p className="status">Could not load your meetings. {error}. Reload to try again.</p>)
  if (!email || !data) return shell(<p className="status">Loading your meetings</p>)

  return shell(
    <>
      <h1>Your meetings</h1>

      <h2>Upcoming</h2>
      {data.upcoming.length === 0 ? (
        <div className="empty">Nothing yet. Add the bot to a meeting and it appears here.</div>
      ) : (
        <table className="meetings">
          <thead>
            <tr>
              <th>Meeting</th>
              <th>Ends</th>
              <th title="Accepted out of invited">Accepted</th>
            </tr>
          </thead>
          <tbody>
            {data.upcoming.map(m => (
              <tr key={m.id}>
                <td className="title-cell">{m.title || <span className="muted">Untitled</span>}</td>
                <td className="num">{formatDate(m.endAt)}</td>
                <td className="num" title={`${m.acceptedCount} accepted out of ${m.attendeeCount} invited`}>
                  {m.acceptedCount} of {m.attendeeCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Past</h2>
      {data.past.length === 0 ? (
        <div className="empty">No past meetings yet. Invite <code>bot@calendarfeedback.com</code> to your next one.</div>
      ) : (
        <table className="meetings">
          <thead>
            <tr>
              <th>Meeting</th>
              <th>Ended</th>
              <th title="Accepted out of invited">Accepted</th>
              <th>Votes</th>
              <th>Score</th>
            </tr>
          </thead>
          <tbody>
            {data.past.map(m => (
              <tr key={m.id}>
                <td className="title-cell">{m.title || <span className="muted">Untitled</span>}</td>
                <td className="num">{formatDate(m.endAt)}</td>
                <td className="num" title={`${m.acceptedCount} accepted out of ${m.attendeeCount} invited`}>
                  {m.acceptedCount} of {m.attendeeCount}
                </td>
                <td className="num">{m.voteCount}</td>
                <td>{renderScore(m)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

function renderScore(m: Meeting) {
  if (m.score === undefined) {
    return <span className="s pending">{m.voteCount === 0 ? 'no votes' : `${m.voteCount} of 3 votes`}</span>
  }
  const cls = m.score >= 70 ? 'good' : m.score >= 50 ? 'mid' : 'bad'
  return <span className={`s ${cls}`}>{m.score}</span>
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}
